"use client";

import { JSX, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "motion/react";
import { cn } from "@/lib/utils";
import { AnimatedBadge } from "@/components/motion/animated-badge";
import { useViewerLanguage } from "@/lib/i18n";
import { emitModuleInteraction } from "@/lib/module-interaction";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import {
  INSIGHT_PANEL_MAX_ITEMS,
  type ModuleInsightItem,
  type ModuleInsightPanelData,
} from "@/types/modules";
import { importsTokenScope } from "./imports-token-scope";
import { formatModuleValue } from "./shared/value-format";
import { INSIGHT_PANEL_ADVANCE_MS, INSIGHT_PANEL_VISIBLE_AMOUNT } from "@/config/timing";

const INSIGHT_TRANSITION = { duration: 0.28, ease: [0.4, 0, 0.2, 1] as const };
const INSIGHT_SLOTS = [1, 2, 3, 4, 5] as const;

function hashString(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pickSlots(rand: () => number, count: number): number[] {
  const pool: number[] = [...INSIGHT_SLOTS];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, count);
}

const pct = (n: number) => `${Math.round(n)}%`;

export function insightGradient(seedKey: string, grayscale?: boolean): string {
  const rand = mulberry32(hashString(seedKey));
  const [base, mid, glowA, glowB] = pickSlots(rand, 4);
  const angle = Math.round(120 + rand() * 120);
  const token = (slot: number) =>
    grayscale ? `var(--handyout-chart-mono-${slot})` : `var(--handyout-chart-${slot})`;
  const color = (slot: number, amount: number) =>
    `color-mix(in oklab, ${token(slot)} ${amount}%, transparent)`;
  const glow = (slot: number, size: number) =>
    `radial-gradient(${pct(size)} ${pct(size * 0.8)} at ${pct(rand() * 100)} ${pct(rand() * 70)}, ${color(slot, 85)} 0%, transparent 70%)`;
  return [
    glow(glowA, 70 + rand() * 30),
    glow(glowB, 60 + rand() * 30),
    `radial-gradient(90% 70% at ${pct(rand() * 100)} 100%, ${color(mid, 90)} 0%, transparent 65%)`,
    `linear-gradient(${angle}deg, ${token(base)} 0%, ${token(mid)} 100%)`,
  ].join(", ");
}

const SCRIM =
  "linear-gradient(to top, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.32) 45%, rgba(0,0,0,0.12) 100%)";

function InsightBody({ item }: { item: ModuleInsightItem }) {
  const { intlLocale } = useViewerLanguage();
  const description = stripInlineMarkdown(item.description);
  return (
    <>
      <span className="text-5xl font-medium leading-none tracking-tighter tabular-nums [overflow-wrap:anywhere] @md:text-6xl">
        {formatModuleValue(item.value, {
          format: item.format,
          digits: item.digits,
          unit: item.unit,
          locale: intlLocale,
        })}
      </span>
      <p className="text-xl font-medium leading-snug text-balance">
        {stripInlineMarkdown(item.headline)}
      </p>
      {description && (
        <p className="text-sm leading-relaxed text-white/80">{description}</p>
      )}
    </>
  );
}

export default function ModuleInsightPanel({
  data,
  seed,
  grayscale,
  accent,
  className,
}: {
  data: ModuleInsightPanelData;
  seed?: string;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t } = useViewerLanguage();
  const items = useMemo(
    () => (data?.items ?? []).slice(0, INSIGHT_PANEL_MAX_ITEMS),
    [data?.items],
  );
  const badge = stripInlineMarkdown(data?.badge) || t("Key insights");
  const seedKey = seed ?? JSON.stringify(items.map((i) => [i.value, i.headline]));
  const background = useMemo(() => insightGradient(seedKey, grayscale), [seedKey, grayscale]);

  const rootRef = useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef, { amount: INSIGHT_PANEL_VISIBLE_AMOUNT });
  const reduceMotion = useReducedMotion() ?? false;

  const [active, setActive] = useState(0);
  const [hovering, setHovering] = useState(false);
  const [stopped, setStopped] = useState(false);
  const index = Math.min(Math.max(0, items.length - 1), active);
  const item = items[index];

  const autoAdvance =
    items.length > 1 && inView && !hovering && !stopped && !reduceMotion;

  useEffect(() => {
    if (!autoAdvance) return;
    const timer = window.setTimeout(
      () => setActive((a) => (a + 1) % items.length),
      INSIGHT_PANEL_ADVANCE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [autoAdvance, active, items.length]);

  const transition = reduceMotion ? { duration: 0 } : INSIGHT_TRANSITION;

  return (
    <div
      ref={rootRef}
      className={cn(
        "@container relative isolate flex min-h-[22rem] w-full flex-col overflow-hidden rounded-2xl text-white",
        className,
      )}
      style={importsTokenScope(grayscale, accent)}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onFocusCapture={() => setHovering(true)}
      onBlurCapture={() => setHovering(false)}
    >
      <div aria-hidden className="absolute inset-0 -z-20" style={{ backgroundImage: background }} />
      <div aria-hidden className="absolute inset-0 -z-10" style={{ backgroundImage: SCRIM }} />

      <div className="flex flex-1 flex-col gap-6 p-6">
        <AnimatedBadge status="neutral" size="sm" showIcon={false} className="w-fit">
          {badge}
        </AnimatedBadge>

        <div className="mt-auto grid" aria-live="polite">
          {items.map((it, i) => (
            <div
              key={`sizer-${it.id ?? i}`}
              aria-hidden
              className="pointer-events-none invisible flex flex-col gap-4 [grid-area:1/1]"
            >
              <InsightBody item={it} />
            </div>
          ))}
          <AnimatePresence mode="wait" initial={false}>
            {item && (
              <motion.div
                key={item.id ?? index}
                initial={{ opacity: 0, y: 6, filter: "blur(6px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                exit={{ opacity: 0, y: -6, filter: "blur(6px)" }}
                transition={transition}
                className="flex flex-col justify-end gap-4 [grid-area:1/1]"
              >
                <InsightBody item={item} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {items.length > 1 && (
          <div className="flex gap-1.5" role="tablist" aria-label={badge}>
            {items.map((it, i) => (
              <button
                key={it.id ?? i}
                type="button"
                role="tab"
                aria-selected={i === index}
                aria-label={t("Insight {index} of {total}", { index: i + 1, total: items.length })}
                onClick={(e) => {
                  setStopped(true);
                  if (i === index) return;
                  emitModuleInteraction(
                    e.currentTarget,
                    "tab_change",
                    { kind: "tab", index: i },
                    { from: index, to: i },
                  );
                  setActive(i);
                }}
                className="group flex h-4 flex-1 items-center"
              >
                <span
                  className={cn(
                    "h-1 w-full rounded-full transition-colors",
                    i === index ? "bg-white" : "bg-white/35 group-hover:bg-white/60",
                  )}
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
